import React from "react"
import { graphql } from "gatsby"
import { withPrismicPreview } from "gatsby-plugin-prismic-previews"
import { useTranslation } from "gatsby-plugin-react-i18next"

import { linkResolver } from "utils"

import { AppDownloadPromo } from '../../../app-download-promo/AppDownloadPromo'
import { CenterHours, CenterHoursProps } from '../../../location/center-hours/CenterHours'
import { CenterInfo, CenterInfoProps } from '../../../location/center-info/CenterInfo'
import { LocationFaq } from '../../../location/faq/Faq'
import { MatchingLocations } from '../../../location/center-lookup/matching-locations/MatchingLocations'
import { Hero } from '../../../location/hero/Hero'
import { Layout } from '../../../layout/Layout'
import { Reviews, ReviewsProps } from '../../../location/reviews/Reviews'
import { Seo } from '../../../seo/Seo'
import { ThreeColumn } from '../../../three-column/ThreeColumn'


type LocalityPageProps = {
  pageContext: {
    matchedLocations: { CenterHoursProps, CenterInfoProps, ReviewsProps }[];
    name: string;
    newDonorBonusAmount: string;
  }
}

const LocalityPage: React.FC<LocalityPageProps> = ( props ) => {
  const { t } = useTranslation("shared", {
    keyPrefix: "location_pages.locality",
  });

  const context = props.pageContext
  const {
    matchedLocations,
    name,
    newDonorBonusAmount,
  } = context
  const defaultBonusAmount = "$130"
  const bonusAmount = newDonorBonusAmount || defaultBonusAmount;
  const heroIntro = t("intro", { bonusAmount });
  
  const firstMatchingLocation = matchedLocations[0];

  return (
    <Layout>
      <Hero intro={heroIntro} title={name} />
      {matchedLocations?.length === 1 && (
        <>
          <CenterInfo {...firstMatchingLocation} />
          {!firstMatchingLocation.comingSoon && (<CenterHours centerHours={firstMatchingLocation.centerHours} />)}
          {firstMatchingLocation.reviews?.length > 0 && (<Reviews reviews={firstMatchingLocation.reviews} />)}
        </>
      )}

      {matchedLocations?.length > 1 && (
        <MatchingLocations useBiggerHeader={true} matchingLocations={matchedLocations} />
      )}
      <ThreeColumn />
      <LocationFaq />
      <AppDownloadPromo />
    </Layout>
  )
}

export const Head = pageData => {
  const { locality, localityRegion, name, url } =
    pageData?.pageContext;
  const canonicalUrl = `https://www.joinparachute.com${url}`

  const jsonLD = {
    "@context": "https://schema.org",
    "@type": "Place",
    address: {
      addressLocality: locality,
      addressRegion: localityRegion,
    },
    url: canonicalUrl,
  }

  return (
    <Seo
      canonicalUrl={canonicalUrl}
      jsonLD={JSON.stringify(jsonLD)}
      ogDescription={`Parachute is a modern plasma donation experience with a location close to ${name}. Earn when you feel like it and download the Parachute app to get started.`}
      ogTitle={`Donate plasma near ${name}`}
      ogType="page"
      title={`Donate plasma near ${name}`}
    />
  )
}


export default withPrismicPreview(LocalityPage, [
  {
    repositoryName: process.env.GATSBY_PRISMIC_REPO_NAME,
    linkResolver,
  },
])

export const localityPageQuery = graphql`
  query ($language: String! = "en") {
    locales: allLocale(
      filter: { ns: { in: ["shared"] }, language: { eq: $language } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
